/**
 * Per-type alert behaviour, set by admins (Andy, 2026-10-13).
 *
 * Every kind of notification gets four switches:
 *   - enabled            — off means nobody is told at all
 *   - urgent             — the red pop-up in the app (and a stay-on-screen
 *                          desktop alert), rather than a toast and a row
 *   - push               — also sent to desktop alerts and the Omni Field phone
 *   - ignoreBeforeClaim  — for types that can be critical ("I've got it"),
 *                          whether Ignore is allowed before anyone claims it
 *
 * The defaults reproduce the behaviour before this panel existed exactly, so
 * shipping it changes nothing until an admin edits a row. One module, shared
 * by the API (what to store/push) and the SPA (what pops up and what can be
 * ignored), so the two cannot disagree.
 */
import { PUSH_NOTIFICATION_KINDS, URGENT_NOTIFICATION_KINDS } from "./priority-ticket-changes.js";
export const ALERT_TYPES = [
    { kind: "servicedesk-priority", label: "P1 / P2 tickets", description: "A ticket raised or escalated to P1 or P2. Everyone is told.", claimable: true, alwaysOn: false },
    { kind: "servicedesk-group-assigned", label: "New ticket for your team", description: "A ticket put in your team's ServiceDesk group with nobody assigned yet.", claimable: false, alwaysOn: false },
    { kind: "servicedesk-assigned", label: "Ticket assigned to you", description: "A ServiceDesk ticket assigned to you.", claimable: false, alwaysOn: false },
    { kind: "servicedesk-updated", label: "Reply or update on your ticket", description: "Someone else replied to or changed a ticket assigned to you.", claimable: false, alwaysOn: false },
    { kind: "risky-user", label: "Risky users", description: "Entra ID Protection flags a user at risk.", claimable: true, alwaysOn: false },
    { kind: "security-incident", label: "Defender incidents", description: "A new medium, high or critical Defender incident.", claimable: true, alwaysOn: false },
    { kind: "security-alert", label: "Defender alerts", description: "A new Defender alert.", claimable: true, alwaysOn: false },
    { kind: "network-incident", label: "Network outages", description: "A site or device down, a WAN uplink failing, a FortiGate disconnected.", claimable: true, alwaysOn: false },
    { kind: "network-restored", label: "Network restored", description: "A site or device back up.", claimable: false, alwaysOn: false },
    { kind: "backup-failed", label: "Backup failed or overdue", description: "An Azure Backup item failed, or has not backed up within its expected window.", claimable: false, alwaysOn: false },
    { kind: "web-filtering-blocks", label: "Web filtering blocks", description: "Hourly summary of high-risk sites Forcepoint blocked.", claimable: false, alwaysOn: false },
    { kind: "service-notice", label: "Service notices", description: "An admin-written known-issue notice published or resolved.", claimable: false, alwaysOn: false },
    { kind: "approval-requested", label: "Approval waiting on you", description: "Someone needs you to approve a change. Cannot be switched off.", claimable: false, alwaysOn: true },
    { kind: "approval-granted", label: "Approval granted", description: "A request you raised was approved.", claimable: false, alwaysOn: false },
    { kind: "approval-rejected", label: "Approval rejected", description: "A request you raised was rejected.", claimable: false, alwaysOn: false },
    { kind: "approval-expired", label: "Approval expired", description: "A request you raised lapsed without a decision.", claimable: false, alwaysOn: false }
];
const TYPE_BY_KIND = new Map(ALERT_TYPES.map((t) => [t.kind, t]));
/** What a kind did before the panel existed. */
export function defaultAlertBehaviour(kind) {
    return {
        enabled: true,
        urgent: URGENT_NOTIFICATION_KINDS.has(kind),
        push: PUSH_NOTIFICATION_KINDS.has(kind),
        ignoreBeforeClaim: false
    };
}
/**
 * The effective behaviour of one kind: stored values over the defaults. An
 * always-on type is always enabled whatever is stored; a kind that cannot be
 * claimed always allows Ignore (there is nothing to wait for).
 */
export function resolveAlertBehaviour(stored, kind) {
    const base = defaultAlertBehaviour(kind);
    const s = stored?.[kind] ?? {};
    const info = TYPE_BY_KIND.get(kind);
    const pick = (v, d) => (typeof v === "boolean" ? v : d);
    return {
        enabled: info?.alwaysOn ? true : pick(s.enabled, base.enabled),
        urgent: pick(s.urgent, base.urgent),
        push: pick(s.push, base.push),
        ignoreBeforeClaim: info && !info.claimable ? true : pick(s.ignoreBeforeClaim, base.ignoreBeforeClaim)
    };
}
/** Every listed type, resolved — what the admin panel and the SPA read. */
export function resolveAllAlertBehaviours(stored) {
    const out = {};
    for (const t of ALERT_TYPES)
        out[t.kind] = resolveAlertBehaviour(stored, t.kind);
    return out;
}
/**
 * Clean an admin's save: known kinds only, real booleans only. Throws with a
 * readable reason on anything else, so a bad body is refused rather than
 * half-applied.
 */
export function normaliseAlertBehaviourMap(input) {
    if (!input || typeof input !== "object" || Array.isArray(input))
        throw new Error("kinds must be an object of alert types.");
    const out = {};
    for (const [kind, value] of Object.entries(input)) {
        if (!TYPE_BY_KIND.has(kind))
            throw new Error(`Unknown alert type "${kind}".`);
        if (!value || typeof value !== "object")
            throw new Error(`Settings for "${kind}" must be an object.`);
        const v = value;
        const entry = {};
        for (const key of ["enabled", "urgent", "push", "ignoreBeforeClaim"]) {
            if (v[key] === undefined)
                continue;
            if (typeof v[key] !== "boolean")
                throw new Error(`"${key}" for "${kind}" must be true or false.`);
            entry[key] = v[key];
        }
        out[kind] = entry;
    }
    return out;
}
